import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import InputField from "components/fields/InputField";
import Dropdown from "components/dropdown";
import { MdFilterList, MdSearch, MdBusiness, MdPerson, MdAdd, MdTrendingUp } from "react-icons/md";
import { FaPlane, FaTrain, FaCar, FaHotel, FaBus, FaPassport } from "react-icons/fa";
import { HiSparkles, HiLightningBolt, HiChartBar } from "react-icons/hi";
// import { Tooltip } from "react-tooltip";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

// Mock data with more records
const mockCarteData = [
  {
    id: 1,
    employeeName: "John Doe",
    companyName: "Tech Corp",
    customerId: "CUST-001",
    carteNo: "CT-2024-001",
    bookingTypes: [
      {type: "flight", details: "AA123 - NYC to LAX"},
      {type: "hotel", details: "Marriott Downtown - 5 nights"},
      {type: "taxi", details: "Airport Transfer"}
    ],
    startDate: "2024-01-15",
    endDate: "2024-01-20",
    destination: "New York",
    totalAmount: "$2,450",
    status: "Active"
  },
  {
    id: 2,
    employeeName: "Jane Smith",
    companyName: "Global Inc",
    customerId: "CUST-002",
    carteNo: "CT-2024-002",
    bookingTypes: [
      {type: "train", details: "Amtrak - Chicago Express"},
      {type: "hotel", details: "Hilton Garden - 4 nights"}
    ],
    startDate: "2024-01-18",
    endDate: "2024-01-22",
    destination: "Chicago",
    totalAmount: "$1,850",
    status: "Pending"
  },
  {
    id: 3,
    employeeName: "Mike Johnson",
    companyName: "Tech Corp",
    customerId: "CUST-003",
    carteNo: "CT-2024-003",
    bookingTypes: [
      {type: "flight", details: "UA456 - LAX to SFO"},
      {type: "train", details: "Caltrain - Local Service"},
      {type: "hotel", details: "Hyatt Regency - 3 nights"},
      {type: "bus", details: "City Bus Pass"}
    ],
    startDate: "2024-01-25",
    endDate: "2024-01-30",
    destination: "Los Angeles",
    totalAmount: "$3,200",
    status: "Completed"
  },
  {
    id: 4,
    employeeName: "Sarah Wilson",
    companyName: "Global Inc",
    customerId: "CUST-004",
    carteNo: "CT-2024-004",
    bookingTypes: [
      {type: "flight", details: "DL789 - Miami to Boston"},
      {type: "taxi", details: "Airport & City Transfer"}
    ],
    startDate: "2024-02-01",
    endDate: "2024-02-05",
    destination: "Miami",
    totalAmount: "$1,950",
    status: "Active"
  },
  {
    id: 5,
    employeeName: "David Brown",
    companyName: "StartUp Ltd",
    customerId: "CUST-005",
    carteNo: "CT-2024-005",
    bookingTypes: [
      {type: "train", details: "Eurostar - London to Paris"},
      {type: "hotel", details: "Le Meridien - 3 nights"},
      {type: "bus", details: "City Tour Pass"}
    ],
    startDate: "2024-02-10",
    endDate: "2024-02-13",
    destination: "Paris",
    totalAmount: "$2,100",
    status: "Pending"
  },
  {
    id: 6,
    employeeName: "Lisa Garcia",
    companyName: "Tech Corp",
    customerId: "CUST-006",
    carteNo: "CT-2024-006",
    bookingTypes: [
      {type: "flight", details: "SW321 - Denver to Seattle"},
      {type: "hotel", details: "Four Seasons - 4 nights"},
      {type: "taxi", details: "Premium Transfer Service"}
    ],
    startDate: "2024-02-15",
    endDate: "2024-02-19",
    destination: "Seattle",
    totalAmount: "$2,800",
    status: "Active"
  }
];

const companyEmployeeMap = {
  "Tech Corp": ["John Doe", "Mike Johnson", "Lisa Garcia"],
  "Global Inc": ["Jane Smith", "Sarah Wilson"],
  "StartUp Ltd": ["David Brown"]
};

const BookingIcon = ({ type }) => {
  const iconProps = { size: 14, className: "text-white" };
  
  switch (type) {
    case "flight": return <FaPlane {...iconProps} />;
    case "train": return <FaTrain {...iconProps} />;
    case "railway": return <FaTrain {...iconProps} />;
    case "taxi": return <FaCar {...iconProps} />;
    case "car": return <FaCar {...iconProps} />;
    case "hotel": return <FaHotel {...iconProps} />;
    case "bus": return <FaBus {...iconProps} />;
    case "visa": return <FaPassport {...iconProps} />;
    default: return null;
  }
};

const BookingTypeStack = ({ bookingTypes }) => {
  const colors = {
    flight: "bg-brand-500",
    train: "bg-brand-500",
    railway: "bg-brand-500", 
    taxi: "bg-brand-500",
    car: "bg-brand-500",
    hotel: "bg-brand-500",
    bus: "bg-brand-500",
    visa: "bg-brand-500"
  };

  // Group bookings by type
  const groupedBookings = bookingTypes.reduce((acc, booking) => {
    if (!acc[booking.type]) {
      acc[booking.type] = [];
    }
    acc[booking.type].push(booking.details);
    return acc;
  }, {});

  return (
    <div className="flex -space-x-1">
      {Object.entries(groupedBookings).map(([type, details], index) => {
        const tooltipContent = `${type.charAt(0).toUpperCase() + type.slice(1)} (${details.length}):\n${details.join('\n')}`;
        
        return (
          <div
            key={type}
            className={`${colors[type]} rounded-full p-2 border-2 border-white shadow-lg cursor-pointer relative hover:scale-110 transition-transform duration-200`}
            style={{ zIndex: Object.keys(groupedBookings).length - index }}
            data-tooltip-id={`booking-${type}`}
            data-tooltip-content={tooltipContent}
          >
            <BookingIcon type={type} />
            {/* <Tooltip id={`booking-${type}`} style={{ whiteSpace: 'pre-line' }} /> */}
          </div>
        );
      })}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    Active: {
      bg: "bg-gradient-to-r from-green-400 to-emerald-500",
      text: "text-white",
      icon: "🟢"
    },
    Pending: {
      bg: "bg-gradient-to-r from-yellow-400 to-amber-500",
      text: "text-white",
      icon: "⏳"
    },
    Completed: {
      bg: "bg-gradient-to-r from-blue-400 to-indigo-500",
      text: "text-white",
      icon: "✅"
    }
  };

  const config = statusConfig[status] || statusConfig.Pending;

  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text} shadow-md flex items-center space-x-1`}>
      <span>{config.icon}</span>
      <span>{status}</span>
    </span>
  );
};

const CarteListing = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  
  // Load saved cartes function
  const loadCartes = React.useCallback(() => {
    const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
    const formattedCartes = savedCartes.map((carte, index) => {
      const allBookings = Object.entries(carte.bookings).reduce((acc, [type, items]) => {
        items.forEach(item => {
          acc.push({ type, details: item.name });
        });
        return acc;
      }, []);
      
      return {
        id: index + 1,
        employeeName: carte.customer.name,
        companyName: "Your Company",
        customerId: carte.customer.id,
        carteNo: `CT-${new Date(carte.timestamp).getFullYear()}-${String(index + 1).padStart(3, '0')}`,
        bookingTypes: allBookings,
        startDate: carte.timestamp.split('T')[0],
        endDate: carte.timestamp.split('T')[0],
        destination: "Multiple",
        totalAmount: "$0",
        status: "Active"
      };
    });
    
    // Combine with mock data
    setData([...formattedCartes, ...mockCarteData]);
  }, []);

  // Load saved cartes on component mount
  React.useEffect(() => {
    loadCartes();
  }, [loadCartes]);

  // Listen for localStorage changes, page focus, and custom events
  React.useEffect(() => {
    const handleStorageChange = () => {
      loadCartes();
    };

    const handleFocus = () => {
      loadCartes();
    };

    const handleCartesUpdated = () => {
      loadCartes();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('cartesUpdated', handleCartesUpdated);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('cartesUpdated', handleCartesUpdated);
    };
  }, [loadCartes]);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [tripDate, setTripDate] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("All Companies");
  const [selectedEmployee, setSelectedEmployee] = useState("All Employees");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const availableEmployees = useMemo(() => {
    if (selectedCompany === "All Companies") {
      return ["All Employees", ...Object.values(companyEmployeeMap).flat()];
    }
    return ["All Employees", ...(companyEmployeeMap[selectedCompany] || [])];
  }, [selectedCompany]);

  const handleCompanyChange = (company) => {
    setSelectedCompany(company);
    setSelectedEmployee("All Employees");
  };

  const columns = useMemo(() => [
    columnHelper.accessor("carteNo", {
      id: "carteNo",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">CARTE NO.</p>
      ),
      cell: (info) => {
        const rowIndex = info.row.index;
        return (
          <button
            onClick={() => {
              navigate(`/admin/booking?carteId=${info.row.original.id}`);
            }}
            className="text-sm font-mono font-bold text-indigo-600 dark:text-white hover:text-indigo-800 cursor-pointer transition-colors duration-200 hover:underline"
          >
            {info.getValue()}
          </button>
        );
      },
    }),
    columnHelper.accessor("employeeName", {
      id: "employeeName",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">EMPLOYEE</p>
      ),
      cell: (info) => (
        <div className="flex items-center min-w-[140px]">
          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs mr-3 shadow-md">
            {info.getValue().split(' ').map(n => n[0]).join('')}
          </div>
          <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("customerId", {
      id: "customerId",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">CUSTOMER ID</p>
      ),
      cell: (info) => (
        <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded-lg text-slate-700 dark:text-white">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("bookingTypes", {
      id: "bookingTypes",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">BOOKINGS</p>
      ),
      cell: (info) => <BookingTypeStack bookingTypes={info.getValue()} />,
    }),
    columnHelper.accessor("destination", {
      id: "destination",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">DESTINATION</p>
      ),
      cell: (info) => (
        <div className="flex items-center space-x-2">
          <span className="text-lg">📍</span>
          <p className="text-sm font-medium text-slate-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("startDate", {
      id: "tripDate",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">TRIP DATE</p>
      ),
      cell: (info) => (
        <div className="flex items-center space-x-2">
          <span className="text-lg">📅</span>
          <p className="text-sm font-medium text-slate-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("totalAmount", {
      id: "totalAmount",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">AMOUNT</p>
      ),
      cell: (info) => (
        <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-slate-600 dark:text-white">STATUS</p>
      ),
      cell: (info) => <StatusBadge status={info.getValue()} />,
    }),
  ], []);

  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchesCompany = selectedCompany === "All Companies" || item.companyName === selectedCompany;
      const matchesEmployee = selectedEmployee === "All Employees" || item.employeeName === selectedEmployee;
      const matchesDate = !tripDate || item.startDate.includes(tripDate) || item.endDate.includes(tripDate);
      
      return matchesCompany && matchesEmployee && matchesDate;
    });
  }, [data, selectedCompany, selectedEmployee, tripDate]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900 p-4">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg">
              <HiChartBar className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
                Carte Management
              </h1>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Manage and track all travel bookings</p>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/admin/booking')}
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center space-x-2">
              <MdAdd className="h-5 w-5" />
              <span>Create New Carte</span>
              <HiLightningBolt className="h-4 w-4 group-hover:animate-pulse" />
            </div>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card extra="p-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Cartes</p>
                <p className="text-2xl font-bold">{filteredData.length}</p>
              </div>
              <HiChartBar className="h-8 w-8 text-blue-200" />
            </div>
          </Card>
          <Card extra="p-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Active</p>
                <p className="text-2xl font-bold">{filteredData.filter(item => item.status === 'Active').length}</p>
              </div>
              <MdTrendingUp className="h-8 w-8 text-green-200" />
            </div>
          </Card>
          <Card extra="p-4 bg-gradient-to-r from-yellow-500 to-amber-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm">Pending</p>
                <p className="text-2xl font-bold">{filteredData.filter(item => item.status === 'Pending').length}</p>
              </div>
              <span className="text-2xl">⏳</span>
            </div>
          </Card>
          <Card extra="p-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Completed</p>
                <p className="text-2xl font-bold">{filteredData.filter(item => item.status === 'Completed').length}</p>
              </div>
              <span className="text-2xl">✅</span>
            </div>
          </Card>
        </div>
      </div>

      {/* Filters Section */}
      <Card extra="w-full p-6 mb-6 bg-white/70 dark:bg-navy-800/70 backdrop-blur-sm border border-white/20 dark:border-navy-700/50 shadow-xl">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <MdFilterList className="text-slate-600 h-5 w-5" />
            <span className="text-sm font-semibold text-slate-700 dark:text-white">Filters:</span>
          </div>
          
          {/* Company Dropdown */}
          <div className="min-w-[180px]">
            <Dropdown
              button={
                <div className="flex h-10 w-full items-center justify-between rounded-xl border-2 border-slate-200 bg-white/50 p-2 text-sm outline-none hover:border-indigo-300 cursor-pointer transition-colors">
                  <div className="flex items-center">
                    <MdBusiness className="mr-2 text-slate-400" size={16} />
                    <span className="truncate">{selectedCompany}</span>
                  </div>
                </div>
              }
              classNames="top-12 right-0 w-full z-[9999]"
            >
              <div className="bg-white border border-slate-200 rounded-xl shadow-xl">
                {["All Companies", ...Object.keys(companyEmployeeMap)].map((company) => (
                  <div
                    key={company}
                    className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm transition-colors first:rounded-t-xl last:rounded-b-xl"
                    onClick={() => handleCompanyChange(company)}
                  >
                    {company}
                  </div>
                ))}
              </div>
            </Dropdown>
          </div>

          {/* Employee Dropdown */}
          <div className="min-w-[180px]">
            <Dropdown
              button={
                <div className="flex h-10 w-full items-center justify-between rounded-xl border-2 border-slate-200 bg-white/50 p-2 text-sm outline-none hover:border-indigo-300 cursor-pointer transition-colors">
                  <div className="flex items-center">
                    <MdPerson className="mr-2 text-slate-400" size={16} />
                    <span className="truncate">{selectedEmployee}</span>
                  </div>
                </div>
              }
              classNames="top-12 right-0 w-full z-[9999]"
            >
              <div className="bg-white border border-slate-200 rounded-xl shadow-xl z-[9999]">
                {availableEmployees.map((employee) => (
                  <div
                    key={employee}
                    className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm transition-colors first:rounded-t-xl last:rounded-b-xl"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    {employee}
                  </div>
                ))}
              </div>
            </Dropdown>
          </div>

          {/* Trip Date */}
          <div className="min-w-[150px]">
            <input
              type="date"
              value={tripDate}
              onChange={(e) => setTripDate(e.target.value)}
              className="h-10 w-full rounded-xl border-2 border-slate-200 bg-white/50 p-2 text-sm outline-none hover:border-indigo-300 transition-colors"
              placeholder="Travel Date"
            />
          </div>
        </div>
      </Card>

      {/* Table Section */}
      <Card extra="w-full px-6 pb-6 bg-white/70 backdrop-blur-sm border border-white/20 shadow-xl">
        <div className="relative flex items-center justify-between pt-6 pb-4">
          <div className="text-2xl font-bold text-slate-800 dark:text-white">
            Carte Listing ({filteredData.length} records)
          </div>
          <div className="relative w-80">
            <input
              type="text"
              placeholder="Search across all fields..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="h-10 w-full rounded-xl border-2 border-slate-200 dark:border-navy-600 bg-white/50 dark:bg-navy-800/50 text-slate-900 dark:text-white pl-3 pr-10 text-sm outline-none hover:border-indigo-300 dark:hover:border-indigo-400 focus:border-indigo-500 transition-colors"
            />
            <MdSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
          </div>
        </div>

        <div className="mt-4 w-full">
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full table-fixed">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-navy-800 dark:to-navy-700">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer px-4 py-4 text-start hover:bg-slate-100 transition-colors"
                        style={{ 
                          width: header.id === 'carteNo' ? '12%' :
                                 header.id === 'employeeName' ? '15%' : 
                                 header.id === 'customerId' ? '12%' : 
                                 header.id === 'bookingTypes' ? '15%' : 
                                 header.id === 'destination' ? '12%' : 
                                 header.id === 'tripDate' ? '15%' :
                                 header.id === 'totalAmount' ? '10%' : '9%' 
                        }}
                      >
                        <div className="items-center justify-between text-xs">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white dark:bg-navy-800">
                {table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id} className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-25'}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="border-t border-slate-100 py-4 px-4"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-navy-700 transition-colors"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                {'<<'}
              </button>
              <button
                className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-navy-700 transition-colors"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {'<'}
              </button>
              <button
                className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-navy-700 transition-colors"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {'>'}
              </button>
              <button
                className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-navy-700 transition-colors"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                {'>>'}
              </button>
            </div>
            <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </strong>
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
              className="border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg p-2 text-sm"
            >
              {[5, 10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CarteListing;